import {
  Component,
  StrictMode,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createRoot } from 'react-dom/client';
import { sessionManifestSchema, type SessionManifest } from '@agentos/contracts';
import {
  AgentOSShell,
  createAgentApi,
  createBrainApi,
  createVoiceApi,
  createContactsApi,
  createPhoneNumbersApi,
  createCampaignsApi,
  createLeadsApi,
  createProviderApi,
  createOperationsApi,
  createIntelligenceApi,
  createPerformanceApi,
  createIntegrationsApi,
  createLiveOpsApi,
  createBillingApi,
  createPlatformAdminApi,
  readColorMode,
  writeColorMode,
  type ColorMode,
} from '@agentos/ui';

const apiBaseUrl = (import.meta.env.VITE_AGENTOS_API_URL ?? '').replace(/\/$/, '');
const ssoSessionUrl = import.meta.env.VITE_PANARYUS_SSO_URL ?? '/sso/session';
const loginUrl = import.meta.env.VITE_PANARYUS_LOGIN_URL ?? 'https://panaryus.com/login';

type SsoSessionResponse = {
  sessionToken?: string;
  token?: string;
};

const redirectToLogin = (): void => {
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${loginUrl}?redirect=${redirect}`;
};

/** Operator-facing TR defaults for known boot failures (API message preferred when present). */
const bootCopy = {
  loadingTitle: 'CenterOS açılıyor',
  loadingBody:
    'Panaryus oturumunuz doğrulanıyor ve kısa ömürlü CenterOS erişim anahtarı hazırlanıyor.',
  errorTitle: 'CenterOS açılamadı',
  errorFallback: 'CenterOS şu an açılamadı. Sayfayı yenileyin veya Panaryus girişi ile tekrar deneyin.',
  notAllowed: 'Bu Panaryus hesabının CenterOS erişimi yok.',
  identityMissing:
    'Bu hesap için CenterOS kimliği eşlenmemiş. Yöneticinizden Panaryus kullanıcınızı bağlatın.',
  ssoUnavailable:
    'CenterOS geçici olarak kullanılamıyor (SSO 502/503). Birkaç saniye bekleyip yenileyin; sürerse centeros-api servisini kontrol edin.',
  sessionExchange: (status: number): string =>
    `Panaryus oturumu CenterOS’a aktarılamadı (HTTP ${status}).`,
  noToken: 'Panaryus CenterOS oturum anahtarı döndürmedi.',
  tokenRejected: 'CenterOS kısa ömürlü oturum anahtarını reddetti. Yeniden giriş yapın.',
  manifestFailed: 'CenterOS oturum manifesti yüklenemedi. Sayfayı yenileyin.',
  retry: 'Yeniden dene',
  goLogin: 'Panaryus girişine git',
} as const;

const ConsoleApp = (): ReactElement => {
  const [manifest, setManifest] = useState<SessionManifest>();
  const [sessionToken, setSessionToken] = useState<string>();
  const [error, setError] = useState<string>();
  const [bootKey, setBootKey] = useState(0);
  const [bootTheme, setBootTheme] = useState<ColorMode>(() => readColorMode());

  useEffect(() => {
    writeColorMode(bootTheme);
  }, [bootTheme]);

  useEffect(() => {
    let cancelled = false;

    const boot = async (): Promise<void> => {
      try {
        const sessionResponse = await fetch(ssoSessionUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
          signal: AbortSignal.timeout(12_000),
        });

        // Unauthenticated or expired Panaryus cookie → login with return URL.
        if (
          sessionResponse.status === 401 ||
          sessionResponse.status === 419 ||
          sessionResponse.status === 302
        ) {
          redirectToLogin();
          return;
        }

        // Authenticated but not entitled / identity not mapped.
        if (sessionResponse.status === 403) {
          let detail: string = bootCopy.notAllowed;
          try {
            const errBody = (await sessionResponse.json()) as { message?: string; code?: string };
            if (errBody.code === 'HOST_IDENTITY_NOT_FOUND') {
              detail = bootCopy.identityMissing;
            } else if (errBody.message) {
              detail = errBody.message;
            }
          } catch {
            /* keep default */
          }
          throw new Error(detail);
        }

        const contentType = sessionResponse.headers.get('content-type') ?? '';
        if (!sessionResponse.ok || !contentType.includes('application/json')) {
          // HTML login / error pages after redirects should not look like a silent console hang.
          if (contentType.includes('text/html')) {
            redirectToLogin();
            return;
          }
          if (sessionResponse.status === 502 || sessionResponse.status === 503) {
            // Prefer JSON body message when Laravel returns structured 502.
            try {
              const errBody = (await sessionResponse.clone().json()) as {
                message?: string;
                code?: string;
              };
              if (errBody.code === 'HOST_IDENTITY_NOT_FOUND') {
                throw new Error(bootCopy.identityMissing);
              }
              if (errBody.code === 'CENTEROS_FORBIDDEN') {
                throw new Error(errBody.message || bootCopy.notAllowed);
              }
              if (errBody.message) {
                throw new Error(errBody.message);
              }
            } catch (inner) {
              if (inner instanceof Error && !inner.message.startsWith('Unexpected')) {
                throw inner;
              }
            }
            throw new Error(bootCopy.ssoUnavailable);
          }
          throw new Error(bootCopy.sessionExchange(sessionResponse.status));
        }

        const sessionPayload = (await sessionResponse.json()) as SsoSessionResponse;
        const token = sessionPayload.sessionToken ?? sessionPayload.token;

        if (!token) {
          throw new Error(bootCopy.noToken);
        }

        const manifestResponse = await fetch(`${apiBaseUrl}/v1/session-manifest`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          signal: AbortSignal.timeout(12_000),
        });

        if (manifestResponse.status === 401) {
          throw new Error(bootCopy.tokenRejected);
        }
        if (!manifestResponse.ok) {
          throw new Error(bootCopy.manifestFailed);
        }

        if (!cancelled) {
          setSessionToken(token);
          setManifest(sessionManifestSchema.parse((await manifestResponse.json()) as unknown));
        }
      } catch (exception) {
        if (!cancelled) {
          setError(exception instanceof Error ? exception.message : bootCopy.errorFallback);
        }
      }
    };

    void boot();

    return () => {
      cancelled = true;
    };
  }, [bootKey]);

  if (manifest && sessionToken) {
    // Drive shell from the real browser path so /platform deep-links open Platform admin
    // instead of falling through to Genel Bakış. Shell owns history.pushState/popstate.
    const pathRoute =
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/\/+$/, '') || undefined
        : undefined;
    return (
      <AgentOSShell
        manifest={manifest}
        initialRoute={pathRoute && pathRoute !== '/' ? pathRoute : undefined}
        brainApi={createBrainApi(apiBaseUrl, sessionToken)}
        agentApi={createAgentApi(apiBaseUrl, sessionToken)}
        voiceApi={createVoiceApi(apiBaseUrl, sessionToken)}
        contactsApi={createContactsApi(apiBaseUrl, sessionToken)}
        phoneNumbersApi={createPhoneNumbersApi(apiBaseUrl, sessionToken)}
        campaignsApi={createCampaignsApi(apiBaseUrl, sessionToken)}
        leadsApi={createLeadsApi(apiBaseUrl, sessionToken)}
        providerApi={createProviderApi(apiBaseUrl, sessionToken)}
        operationsApi={createOperationsApi(apiBaseUrl, sessionToken)}
        intelligenceApi={createIntelligenceApi(apiBaseUrl, sessionToken)}
        performanceApi={createPerformanceApi(apiBaseUrl, sessionToken)}
        integrationsApi={createIntegrationsApi(apiBaseUrl, sessionToken)}
        liveOpsApi={createLiveOpsApi(apiBaseUrl, sessionToken)}
        billingApi={createBillingApi(apiBaseUrl, sessionToken)}
        platformAdminApi={createPlatformAdminApi(apiBaseUrl, sessionToken)}
      />
    );
  }

  const isError = Boolean(error);
  const bootStyles = bootTheme === 'light' ? bootStylesLight : bootStylesDark;

  return (
    <main style={bootStyles.page} lang="tr" data-theme={bootTheme}>
      <section
        style={bootStyles.card}
        role="status"
        aria-live="polite"
        aria-busy={!isError}
        aria-label={isError ? bootCopy.errorTitle : bootCopy.loadingTitle}
      >
        <div style={bootStyles.themeRow}>
          <div style={bootStyles.mark} aria-hidden="true">
            C
          </div>
          <div style={bootStyles.themeToggle} role="group" aria-label="Renk modu">
            <button
              type="button"
              style={bootTheme === 'dark' ? bootStyles.themeBtnActive : bootStyles.themeBtn}
              onClick={() => setBootTheme('dark')}
            >
              Koyu
            </button>
            <button
              type="button"
              style={bootTheme === 'light' ? bootStyles.themeBtnActive : bootStyles.themeBtn}
              onClick={() => setBootTheme('light')}
            >
              Açık
            </button>
          </div>
        </div>
        <small style={bootStyles.eyebrow}>CENTEROS · PANARYUS</small>
        <h1 style={bootStyles.title}>{isError ? bootCopy.errorTitle : bootCopy.loadingTitle}</h1>
        <p style={bootStyles.copy}>{error ?? bootCopy.loadingBody}</p>
        {!isError && (
          <div style={bootStyles.progressTrack} aria-hidden="true">
            <div style={bootStyles.progressBar} />
          </div>
        )}
        {isError && (
          <div style={bootStyles.actions}>
            <button
              type="button"
              style={bootStyles.button}
              onClick={() => {
                setError(undefined);
                setManifest(undefined);
                setSessionToken(undefined);
                setBootKey((key) => key + 1);
              }}
            >
              {bootCopy.retry}
            </button>
            <button type="button" style={bootStyles.buttonGhost} onClick={redirectToLogin}>
              {bootCopy.goLogin}
            </button>
          </div>
        )}
      </section>
    </main>
  );
};

type BootStyles = Readonly<Record<string, React.CSSProperties>>;

const bootStylesDark: BootStyles = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    color: '#f2f4f8',
    background: 'linear-gradient(160deg,#042f2e 0%,#0f172a 45%,#134e4a 100%)',
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  },
  card: {
    width: 'min(460px,100%)',
    background: '#111620',
    border: '3px solid #0d9488',
    borderRadius: 18,
    padding: '32px',
    boxShadow: '0 28px 90px rgba(0,0,0,.45)',
  },
  themeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  themeToggle: {
    display: 'inline-flex',
    border: '1px solid #2d3748',
    borderRadius: 99,
    overflow: 'hidden',
    background: '#0b0f17',
  },
  themeBtn: {
    border: 0,
    background: 'transparent',
    color: '#8490a4',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  themeBtnActive: {
    border: 0,
    background: '#0d9488',
    color: '#fff',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  mark: {
    width: 48,
    height: 48,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 12,
    marginBottom: 12,
    background: 'linear-gradient(135deg,#0d9488,#f59e0b)',
    fontWeight: 900,
    fontSize: 20,
    color: '#fff',
  },
  eyebrow: {
    color: '#5eead4',
    letterSpacing: '.14em',
    fontWeight: 800,
    fontSize: 11,
    textTransform: 'uppercase' as const,
  },
  title: {
    fontSize: 28,
    margin: '10px 0 0',
    letterSpacing: '-0.02em',
    fontWeight: 800,
    lineHeight: 1.2,
    color: '#f8fafc',
  },
  copy: { color: '#94a3b8', lineHeight: 1.65, fontSize: 14, margin: '12px 0 0' },
  progressTrack: {
    marginTop: 22,
    height: 6,
    borderRadius: 99,
    background: '#1c2333',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    width: '42%',
    borderRadius: 99,
    background: 'linear-gradient(90deg,#f59e0b,#0d9488)',
    animation: 'centeros-boot-pulse 1.2s ease-in-out infinite',
  },
  actions: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginTop: 20 },
  button: {
    border: 0,
    borderRadius: 10,
    padding: '12px 16px',
    color: '#fff',
    background: 'linear-gradient(135deg,#0d9488,#0e7490)',
    fontWeight: 800,
    cursor: 'pointer',
    minHeight: 42,
  },
  buttonGhost: {
    border: '1px solid #2d3748',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#e2e8f0',
    background: 'transparent',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 42,
  },
};

const bootStylesLight: BootStyles = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    color: '#0f172a',
    background: 'linear-gradient(160deg,#ecfdf5 0%,#f4f1ea 50%,#fff7ed 100%)',
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  },
  card: {
    width: 'min(460px,100%)',
    background: '#ffffff',
    border: '3px solid #0d9488',
    borderRadius: 18,
    padding: '32px',
    boxShadow: '0 28px 90px rgba(15,23,42,.12)',
  },
  themeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  themeToggle: {
    display: 'inline-flex',
    border: '1px solid #d6d3d1',
    borderRadius: 99,
    overflow: 'hidden',
    background: '#fafaf9',
  },
  themeBtn: {
    border: 0,
    background: 'transparent',
    color: '#57534e',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  themeBtnActive: {
    border: 0,
    background: '#0d9488',
    color: '#fff',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  mark: {
    width: 48,
    height: 48,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 12,
    marginBottom: 12,
    background: 'linear-gradient(135deg,#0d9488,#f59e0b)',
    fontWeight: 900,
    fontSize: 20,
    color: '#fff',
  },
  eyebrow: {
    color: '#0f766e',
    letterSpacing: '.14em',
    fontWeight: 800,
    fontSize: 11,
    textTransform: 'uppercase' as const,
  },
  title: {
    fontSize: 28,
    margin: '10px 0 0',
    letterSpacing: '-0.02em',
    fontWeight: 800,
    lineHeight: 1.2,
    color: '#0f172a',
  },
  copy: { color: '#334155', lineHeight: 1.65, fontSize: 14, margin: '12px 0 0' },
  progressTrack: {
    marginTop: 22,
    height: 6,
    borderRadius: 99,
    background: '#d6d3d1',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    width: '42%',
    borderRadius: 99,
    background: 'linear-gradient(90deg,#f59e0b,#0d9488)',
    animation: 'centeros-boot-pulse 1.2s ease-in-out infinite',
  },
  actions: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginTop: 20 },
  button: {
    border: 0,
    borderRadius: 10,
    padding: '12px 16px',
    color: '#fff',
    background: 'linear-gradient(135deg,#0d9488,#0e7490)',
    fontWeight: 800,
    cursor: 'pointer',
    minHeight: 42,
  },
  buttonGhost: {
    border: '1px solid #0f766e',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#0f172a',
    background: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 42,
  },
};

// Inject keyframes once (boot screen only; shell has its own style block).
if (typeof document !== 'undefined' && !document.getElementById('centeros-boot-keyframes')) {
  const style = document.createElement('style');
  style.id = 'centeros-boot-keyframes';
  style.textContent = `@keyframes centeros-boot-pulse{0%{transform:translateX(-40%)}50%{transform:translateX(120%)}100%{transform:translateX(260%)}}`;
  document.head.appendChild(style);
}

/** Prevent pure-black empty root when a render throws after boot. */
class ConsoleErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  public constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  public static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('CenterOS console crash', error, info.componentStack);
  }

  public render(): ReactNode {
    if (this.state.error) {
      return (
        <main
          lang="tr"
          style={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            background: '#0f172a',
            color: '#f2f4f8',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          <section
            style={{
              maxWidth: 460,
              width: '100%',
              border: '2px solid #f0c674',
              borderRadius: 16,
              padding: 28,
              background: '#111827',
            }}
          >
            <small style={{ color: '#f0c674', letterSpacing: '0.12em', fontWeight: 800 }}>
              CENTEROS · HATA
            </small>
            <h1 style={{ margin: '10px 0 8px', fontSize: 22 }}>Konsol yüklenemedi</h1>
            <p style={{ color: '#aab3c3', fontSize: 14, lineHeight: 1.5 }}>
              Sayfa çöktü. Genelde eski JS önbelleği veya oturum kaynaklıdır. Hard refresh (Ctrl+Shift+R)
              deneyin; sürerse Panaryus’tan yeniden giriş yapın.
            </p>
            <pre
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 8,
                background: '#0b0f17',
                color: '#ff9d9d',
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </pre>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                style={{
                  border: 0,
                  borderRadius: 10,
                  padding: '10px 14px',
                  background: '#0d9488',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
                onClick={() => window.location.reload()}
              >
                Yenile
              </button>
              <button
                type="button"
                style={{
                  border: '1px solid #30394a',
                  borderRadius: 10,
                  padding: '10px 14px',
                  background: 'transparent',
                  color: '#e8eaf0',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  window.location.href = 'https://panaryus.com/login';
                }}
              >
                Panaryus girişi
              </button>
            </div>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ConsoleErrorBoundary>
      <ConsoleApp />
    </ConsoleErrorBoundary>
  </StrictMode>,
);
